import { useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button } from '@blueprint/ui';
import { NavLink, useParams } from "react-router-dom";

import profileApi from "../../api/profile";
import { Loader, toast, toastError } from "../../common/components/core";
import { GenericError, composeErrorHandlers } from "../../utils/errorHandling";

import { ProfileDetail } from "./components/ProfileDetail";
import UpdateProfileForm, {
    UpdateProfileFormValues,
} from "./components/UpdateProfileForm";
import useProfile from "./hooks/useProfile";
import { isApiError } from "../../api/common";
import { PageTitle } from "../../types/pageTitle";
import { UserProfileType } from "../../types/profile";

export type Profile = {
    id?: number;
    profileName: string;
    profileType: string;
    profileCountryCity: string;
    contactPersonName: string;
    contactPersonNumber: string;
    contactPersonEmail: string;
};

const ProfileDetailView = (): JSX.Element => {
    document.title = PageTitle.ProfileDetailView;

    const [isSubmitting, setSubmitting] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const { profileId } = useParams<{ profileId: string }>();
    const { profile, isValidating, mutate } = useProfile(profileId);
    const onClickEdit = () => {
        setIsEditMode(true);
    };
    const updateProfile = async (values: UpdateProfileFormValues) => {
        if (!values || !profile?.id) {
            return;
        }
        try {
            await profileApi.update(profile.id, values);
            setSubmitting(false);
            setIsEditMode(false);
            mutate();
            toast("Profile Information Updated");
        } catch (e) {
            composeErrorHandlers((next) => (e) => {
                if (isApiError(e) && e.status === 400) {
                    toastError(
                        "Cannot update profile type with the assigned user",
                    );
                } else {
                    next(e);
                }
            })(e as GenericError);
            setSubmitting(false);
            setIsEditMode(false);
            mutate();
        }
    };

    return (
        <Loader isLoading={isValidating || isSubmitting}>
            {profile && (
                <>
                    <div className="d-flex align-items-center justify-content-between mb-5">
                        <Breadcrumb className="mt-3">
                            <Breadcrumb.Item linkAs="span">
                                <NavLink to="/profile-management">
                                    Profile Management
                                </NavLink>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active className="fw-bold">
                                Profile Information
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        {!isEditMode && profile.profileType !== UserProfileType.DEVELOPER && profile.profileType !== UserProfileType.MAIN_CONTRACTOR && (
                            <div>
                                <Button variant="light.primary" onClick={onClickEdit}>
                                    Edit
                                </Button>
                            </div>
                        )}
                    </div>
                    {isEditMode ? (
                        <UpdateProfileForm
                            data={profile}
                            onCancel={() => setIsEditMode(false)}
                            onSubmit={updateProfile}
                        />
                    ) : (
                        <ProfileDetail data={profile} />
                    )}
                </>
            )}
        </Loader>
    );
};

export default ProfileDetailView;
